// LoginForm.js
import { useMemo, useState } from "react";

/**
 * Props:
 * - onSubmit: (payload: { longUrl, domain, alias }) => Promise<void> | void
 * - domains?: string[]  (defaults to ["tinyurl.com"])
 * - initialValues?: { longUrl?: string; domain?: string; alias?: string }
 * - busy?: boolean
 * - className?: string
 */
export default function Shrtr({
  onSubmit,
  domains = ["tinyurl.com"],
  initialValues = {},
  busy = false,
  className = "",
}) {
  const [longUrl, setLongUrl] = useState(initialValues.longUrl ?? "");
  const [domain, setDomain] = useState(
    initialValues.domain ?? domains[0] ?? ""
  );
  const [alias, setAlias] = useState(initialValues.alias ?? "");
  const [errors, setErrors] = useState({ form: "", url: "", alias: "" });

  const aliasRegex = useMemo(() => /^[A-Za-z0-9-_]{0,64}$/, []);

  const validate = () => {
    const next = { form: "", url: "", alias: "" };

    // URL validation
    if (!longUrl.trim()) {
      next.url = "Please enter a URL.";
    } else {
      try {
        // Throws on invalid URL
        // Allow protocol-less input by trying to prefix https://
        // if user omitted it.
        // eslint-disable-next-line no-new
        new URL(longUrl);
      } catch {
        try {
          // try again with https://
          // eslint-disable-next-line no-new
          new URL(`https://${longUrl}`);
        } catch {
          next.url = "That doesn’t look like a valid URL.";
        }
      }
    }

    // Alias validation (optional, but if present must match)
    if (alias && !aliasRegex.test(alias)) {
      next.alias =
        "Use only letters, numbers, hyphens, or underscores (max 64 chars).";
    }

    setErrors(next);
    return !next.url && !next.alias && !next.form;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      longUrl: normalizeUrl(longUrl),
      domain,
      alias: alias.trim() || "",
    };

    try {
      await onSubmit?.(payload);
    } catch (err) {
      setErrors((prev) => ({
        ...prev,
        form:
          err?.message ||
          "Something went wrong while creating your short link. Please try again.",
      }));
    }
  };

  return (
    <div className={`d-flex flex-column ${className}`}>
      <div
        className="d-flex align-items-start flex-column flex-lg-row"
        style={{ maxWidth: 1200 }}
      >
        <div className="page-form text-white w-100">
          <div className="card rounded-10px bg-gray-100 text-dark mb-0">
            <div className="card-body p-0">
              <div
                role="dialog"
                className="py-3 px-3"
                data-test-id="homepage_create_tinyurl_form"
              >
                {/* Hidden anti-autofill helper */}
                <input
                  autoComplete="off"
                  name="hidden-non-bulk"
                  type="text"
                  className="d-none"
                  aria-hidden="true"
                  tabIndex={-1}
                />

                <form autoComplete="off" onSubmit={handleSubmit} noValidate>
                  {/* Long URL */}
                  <div className="form-group mb-4">
                    <label
                      htmlFor="to-shorten-field"
                      className="font-weight-seminormal flex-center mb-3"
                    >
                      <span className="ml-1">Shorten a long URL</span>
                    </label>

                    <input
                      id="to-shorten-field"
                      name="to-shorten-field"
                      type="text"
                      aria-label="Long URL"
                      placeholder="Enter long link here"
                      className="form-control toned-placeholder bg-transparent rounded-xl text-green py-4"
                      value={longUrl}
                      onChange={(e) => setLongUrl(e.target.value)}
                      disabled={busy}
                      autoComplete="off"
                      inputMode="url"
                    />

                    {/* Form + URL error areas (kept for parity with the HTML provided) */}
                    {!!errors.form && (
                      <span
                        role="alert"
                        className="form-error text-break d-block mt-2"
                        data-test-id="homepage_create_tinyurl_validation_form"
                      >
                        {errors.form}
                      </span>
                    )}

                    {!!errors.url && (
                      <span
                        role="alert"
                        className="form-error text-break d-block mt-2"
                        data-test-id="homepage_create_tinyurl_validation_url"
                      >
                        {errors.url}
                      </span>
                    )}
                  </div>

                  {/* Domain + Alias */}
                  <div className="form-group mb-4">
                    <label
                      htmlFor="domain"
                      className="font-weight-seminormal mb-3"
                    >
                      <i
                        className="fa-solid fa-fw fa-wand-magic-sparkles"
                        aria-hidden="true"
                      />
                      <span className="ml-1">Customize your link</span>
                    </label>

                    <div className="form-row mb-0">
                      {/* Domain select */}
                      <div className="col-md-7 mb-2 mb-md-0">
                        <select
                          id="domain"
                          className="form-control rounded-xl py-4"
                          value={domain}
                          onChange={(e) => setDomain(e.target.value)}
                          disabled={busy}
                          aria-label="Domain"
                        >
                          {domains.map((d) => (
                            <option key={d} value={d}>
                              {d}
                            </option>
                          ))}
                        </select>

                        {/* Keep an error slot for domain if you later add validation */}
                        <span
                          role="alert"
                          id="homepage_create_tinyurl_validation_domain"
                          className="form-error text-break"
                          style={{ display: "none" }}
                          aria-hidden="true"
                        />
                      </div>

                      {/* Alias input */}
                      <div className="col-md-5 mb-0">
                        <div tabIndex={0}>
                          <input
                            id="alias"
                            name="alias"
                            type="text"
                            aria-label="Alias"
                            placeholder="Enter alias"
                            className="form-control toned-placeholder bg-transparent rounded-xl text-green py-4"
                            value={alias}
                            onChange={(e) => setAlias(e.target.value)}
                            disabled={busy}
                            autoComplete="off"
                            inputMode="text"
                          />
                        </div>

                        {!!errors.alias && (
                          <span
                            role="alert"
                            className="form-error text-break d-block mt-2"
                            data-test-id="homepage_create_tinyurl_validation_alias"
                          >
                            {errors.alias}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Submit */}
                  <div className="form-group mb-0">
                    <button
                      type="submit"
                      className="btn btn-block font-weight-seminormal btn-xl btn-green"
                      data-test-id="home_shortener_btn_create"
                      disabled={busy}
                    >
                      {busy ? "Working…" : "Shorten URL"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>

          {/* Agreement text */}
          <div className="mt-4 px-2">
            <div
              className="text-xs mb-4"
              data-test-id="create_tinyurl_agreement_text"
            >
              By clicking Shorten URL, I agree to the{" "}
              <a href="/app/terms" className="text-white text-underline">
                <span>Terms of Service</span>
              </a>
              ,{" "}
              <a
                href="/app/privacy-policy"
                className="text-white text-underline"
              >
                <span>Privacy Policy</span>
              </a>{" "}
              and Use of Cookies.
            </div>
            <hr
              className="d-lg-none"
              style={{
                borderTopColor: "rgba(255, 255, 255, 0.5)",
                borderTopWidth: 0.5,
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

/** Normalize user-entered URLs (adds https:// if missing) */
function normalizeUrl(input) {
  const trimmed = input.trim();
  try {
    // valid with scheme
    const u1 = new URL(trimmed);
    return u1.toString();
  } catch {
    try {
      // try adding https
      const u2 = new URL(`https://${trimmed}`);
      return u2.toString();
    } catch {
      // return raw; validation will have already failed in that case
      return trimmed;
    }
  }
}
